import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'

import { columns } from './components/cases-columns'
import { CasesDialogs } from './components/cases-dialogs'
import { CasesPrimaryButtons } from './components/cases-primary-buttons'
import CasesProvider from './context/client-context'
import { Case, caseSchema } from './data/schema'
import { useTranslation } from 'react-i18next'
import { get_cases } from '@/api/case'
import { useEffect, useState } from 'react'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { DataTableToolbar } from './components/data-table-toolbar'
import { DataTable } from '@/components/table'
import { useNavigate, useMatches, useRouter, useSearch } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { filterNonNullValues } from '@/lib/utils'
import { IconChecklist } from '@tabler/icons-react'

declare module '@tanstack/react-router' {
  interface Register {
    HistoryState: {
      caseData?: Case
    }
  }
}

export interface CasesTableParams {
  page: number
  page_size: number
  ordering: string | null
  name: string | null
  case_id?: string | null
  types: string[] | null
  is_archived: boolean | null
  client: string | null
  assigned_to: string | null
  status?: string[] | null
  is_favorite?: boolean | null
}

export default function Cases() {
  const i18n = useTranslation()
  const navigate = useNavigate()
  const matches = useMatches()
  const router = useRouter();
  const search = useSearch({ strict: false });

  // Check if the current route is exactly the cases list route (not a child route like case detail)
  const isExactCasesRoute = matches.some(match => match.routeId === '/_authenticated/cases/' && !match.pathname.includes('/$id'))
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [pagesCnt, setPagesCnt] = useState<number>(0);
  const [params, setParams] = useState<CasesTableParams>({
    page: search?.page || 0,
    page_size: search?.page_size || 10,
    ordering: search?.ordering || null,
    name: search?.name || null,
    case_id: search?.case_id || null,
    types: search?.types ?? null,
    is_archived: search?.is_archived !== undefined ? search.is_archived === true : null,
    client: search?.client || null,
    assigned_to: search?.assigned_to || null,
    status: search?.status || null,
    is_favorite: search?.is_favorite !== undefined ? search.is_favorite === true : null
  });

  useEffect(() => {
    router.navigate({
      to: ".",
      search: filterNonNullValues({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
        name: params.name,
        case_id: params.case_id,
        types: params.types,
        is_archived: params.is_archived,
        client: params.client,
        assigned_to: params.assigned_to,
        status: params.status,
        is_favorite: params.is_favorite
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Handle row click to navigate to case details
  const handleRowClick = (row: Case) => {
    navigate({
      to: `/cases/${row.id}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { caseData: row, timestamp: Date.now() } as any
    })
  }

  // Handle case update in the table
  const handleCaseUpdate = (updatedCase: Case) => {
    setCases(prevCases =>
      prevCases.map(c => c.id === updatedCase.id ? updatedCase : c)
    );
  };

  // Parse user list
  useEffect(() => {
    // Check if we're on the exact cases list route
    if (isExactCasesRoute) {
      get_cases(params).then((resp) => {
        // Robust validation to prevent single-record errors from crashing the entire table
        const validCases: Case[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resp.results.forEach((item: any) => {
          const result = caseSchema.safeParse(item);
          if (result.success) {
            validCases.push(result.data);
          } else {
            console.error(`Case (ID: ${item?.id}) failed schema validation:`, result.error);
          }
        });

        if (validCases.length < resp.results.length) {
          console.warn(`Only ${validCases.length} out of ${resp.results.length} cases were valid.`);
        }

        setCases(validCases)
        setPagesCnt(Math.ceil(resp.count / params.page_size))
        setLoading(false)
        setError(false)
      }).catch((err) => {
        console.error("Failed to fetch cases:", err);
        setError(true)
        setLoading(false)
      });
    } else {
      // If we're not on the cases list route, set loading to false
      setLoading(false);
    }
  }, [params, isExactCasesRoute]);

  return (
    <CasesProvider>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <IconChecklist className="h-5 w-5" />
            </span>
          }
          title={i18n.t('case_list')}
          description={i18n.t('case_list_desc')}
          actions={loading ? <Skeleton className="h-9 w-32" /> : <CasesPrimaryButtons />}
        />
        <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
          {
            loading ? <DataTableSkeleton columns={5} rows={5} />
              : error ?
                <div className='py-8 text-center'>
                  {i18n.t('table_load_error')}
                </div>
                : <DataTable
                  data={cases}
                  columns={columns}
                  params={params}
                  setParams={setParams}
                  pages={pagesCnt}
                  ToolbarName={DataTableToolbar}
                  onRowClick={handleRowClick}
                  storageKey="cases-table"
                />
          }
        </div>
      </Main>

      <CasesDialogs params={params} setParams={setParams} onCaseUpdate={handleCaseUpdate} />
    </CasesProvider>
  )
}
